import React, { useState, useEffect, useRef } from 'react';
import PubSub from 'pubsub-js';
import ReactGA from 'react-ga4';
import Upload from "./Common/CommonUpload/upload/Upload";
import Grid from '@material-ui/core/Grid';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardActions from '@material-ui/core/CardActions';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import { useHistory } from 'react-router-dom';
import Auth from '../services/auth';
import BatchCodingUploadsTable from './BatchCodingUploadTable';
import { uploadTranscriptionDocument, getUploadPolicy } from '../services/transcriptionService';

const auth = new Auth();

// Visible only when the Transcription policy for the selected account allows upload
// (global switch on and account opted in).
const isTranscriptionEnabled = result => !!(result && result.data && Array.isArray(result.data.Policies)
    && result.data.Policies.some(p => p && p.Workflow === 'Transcription' && p.UploadEnabled === true));

const useStyles = makeStyles((theme) => ({
    paper: {
        padding: theme.spacing(2),
        color: theme.palette.text.secondary,
    },
}));

// Mirrors DocumentUploadView's Batch Coding Upload section, pointed at the Transcription Upload
// intake endpoint and filtered status table -- extends the same Upload/BatchCodingUploadsTable
// components via props rather than forking them.
const TranscriptionUploadView = () => {
    const classes = useStyles();
    const history = useHistory();
    const [canView, setCanView] = useState(false);
    const checkVersion = useRef(0);

    const checkCanViewTranscriptionUpload = async () => {
        const version = ++checkVersion.current;
        const account = auth.getActiveAccount();
        setCanView(false);
        // Transcription Upload is for home-account 998 users only, on accounts where it is enabled.
        if (auth.getHomeAccount() !== '998') {
            history.push('/');
            return;
        }
        // Defensive re-check, same as the menu item's own visibility gate -- never rely on the
        // successful authorized policy access controls account visibility, independently of upload availability.
        try {
            const result = await getUploadPolicy(account);
            if (version !== checkVersion.current || account !== auth.getActiveAccount()) return;
            const enabled = isTranscriptionEnabled(result);
            if (!enabled) {
                history.push('/');
                return;
            }
            setCanView(true);
        } catch (error) {
            if (version !== checkVersion.current || account !== auth.getActiveAccount()) return;
            history.push('/');
        }
    };

    useEffect(() => {
        checkCanViewTranscriptionUpload();
        const subscription = PubSub.subscribe('AccountChanged', checkCanViewTranscriptionUpload);
        const changing = PubSub.subscribe('AccountChanging', () => { checkVersion.current++; setCanView(false); });
        ReactGA.send({ hitType: 'pageview', page: '/transcription-upload' });
        return () => { checkVersion.current++; PubSub.unsubscribe(subscription); PubSub.unsubscribe(changing); };
    }, []);

    if (!canView) {
        return null;
    }

    return (
        <Grid container>
            <Grid item xs={12}>
                <Typography variant="h5" gutterBottom>
                    Transcription Uploads
                </Typography>
                <Typography variant="subtitle2" gutterBottom style={{ fontStyle: 'italic' }}>
                    Upload a scanned/faxed document and have it OCR'd, matched to a patient, and turned into a typed transcription job automatically.
                </Typography>
            </Grid>
            <Grid item xs={6}>
                <Grid container spacing={4} direction="column" justify="center">
                    <Grid item>
                        <Card>
                            <CardContent>
                                <Upload workflow="Transcription" selectByKey caption="Upload File" uploadFn={uploadTranscriptionDocument} additionalWorktypes={[]} />
                            </CardContent>
                            <CardActions></CardActions>
                        </Card>
                        <Typography variant="subtitle2" gutterBottom style={{ fontStyle: 'italic', marginTop: '16px' }}>
                            *Do not upload more than 5 files at a time. Please ensure that each file you upload is no larger than 80 MB.
                        </Typography>
                    </Grid>
                    <Grid item>
                        <Typography variant="h6" gutterBottom>
                            Transcription Upload Status
                        </Typography>
                    </Grid>
                    <Grid item>
                        <BatchCodingUploadsTable uploadSource="TranscriptionUpload" />
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    );
};

export default TranscriptionUploadView;



// WEBPACK FOOTER //
// ./src/components/TranscriptionUploadView.js